import { ClassAttributes, Component, useState, useLayoutEffect } from 'react';
import { History } from 'history';
// import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import styled from '@emotion/styled';
import { MuiThemeProvider, CssBaseline } from '@material-ui/core';
import { ApolloClient, NormalizedCacheObject, ApolloProvider, gql } from '@apollo/client';

import theme from '../theme';
import Config from '../core/Config';
import Dependency from '../core/Dependency';
import State from '../core/State';
import GlobalStyles from '../GlobalStyle';
import { LocalizationProvider as MuiPickersUtilsProvider } from '@material-ui/pickers'; //v4-alpha migration
import { Provider as ConfigProvider } from './context/config';
import { Provider as DependencyProvider } from './context/dependency';
import { Provider as HistoryProvider } from './context/history';
import { Provider as StateProvider } from './context/state';
import { Provider as GoogleOauthProvider, GoogleOauthService } from './context/googleOauth';
import LuxonUtils from '@date-io/luxon';
import SigningInIndicatorScreen from './component/primitive/SigningInIndicatorScreen';
import { TOPBAR_HEIGHT } from './component/compound/TopBar';
import DocumentTitle from './connected/DocumentTitle';
import _FatalErrorScreen from './connected/FatalErrorScreen';
import _FormattedSnackbar from './connected/CustomSnackbar';
import NonFatalErrorSnackbars from './connected/NonFatalErrorSnackbars';
import _SignedInScreen from './connected/SignedInScreen';
import _SignedOutScreen from './connected/SignedOutScreen';
import ReactiveSnackbar from './component/compound/ReactiveSnackbar';
import globalState from '../operations/commands';
import { lastAccessedOrganizationVar, availableOrganizationsVar } from '../cache';

const CurrentUserDocument = gql`
  query currentUser {
    currentUser {
      id
      name
      profileImageUrl
      email
    }
  }
`;

const GetAvailableOrganizationsDocument = gql`
  query GetAvailableOrganizations(
    $permissionActions: [OrganizationPermissionActionEnum!]
    $onlyChildOrganizations: Boolean
  ) {
    organizations(permissionActions: $permissionActions, onlyChildOrganizations: $onlyChildOrganizations) {
      id
      name
    }
  }
`;

interface Props {
  config: Config;
  dependency: Dependency;
  history: History;
  state: State;
  apolloClient: ApolloClient<NormalizedCacheObject>;
  googleOauthService: GoogleOauthService;
}
interface RootState {
  loading: boolean;
}

// const config: Config = (window as any).__hugotconfig__;
// const K2_BASE_URL = config.K2_BASE_URL ?? '';
// const history = createBrowserHistory();

class Root extends Component<ClassAttributes<HTMLElement> & Props, RootState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
    };
  }

  async componentDidMount() {
    Promise.all([
      this.props.apolloClient
        .query({
          query: CurrentUserDocument,
          fetchPolicy: 'network-only',
          errorPolicy: 'ignore',
          context: { clientName: 'id' },
        })
        .then((result: any) => {
          // 2022/May/29 currentUser is null even though the response is correctly returning type User
          // potentially a bug in Apollo Client
          globalState.setCurrentUser(result.data?.me);
        }),
      this.props.apolloClient
        .query({
          query: GetAvailableOrganizationsDocument,
          variables: { permissionActions: ['ORGANIZATIONS_READ'], onlyChildOrganizations: true },
          context: { clientName: 'id' },
        })
        .then((result: any) => {
          globalState.setAvailableOrganizations(result.data.organizations);
          const org_param = new URLSearchParams(window.location.search).get('org');
          const preferredOrganization = result.data.organizations.find(({ id }: { id: string }) => org_param === id);
          if (!lastAccessedOrganizationVar()) {
            globalState.setLastAccessedOrganization(preferredOrganization ?? result.data.organizations[0]);
          }
          if (org_param && preferredOrganization) {
            globalState.setLastAccessedOrganization(preferredOrganization);
          }
          if (result.data.organizations.length === 0) {
            globalState.disposeLastAccessedOrganization();
          }
        }),
    ])
      .catch((error: Error) => {
        // no need to set it here ? or no need of onError link in the apollo client ?
        console.log('intial data fetch failed', error);
      })
      .finally(() => {
        this.setState({ loading: false });
      });

    await this.props.dependency.authenticationUsecase.signInFromPreviousSession();
    await this.props.dependency.organizationUsecase.setLastAccessedOrganization();
    await this.props.dependency.organizationUsecase.setOrganizationIdToURL();

    const getOrganizationFromUrlPath = (pathString: string): string => {
      let paths = pathString.split('/');
      for (let i = 0; i < paths.length; i++) {
        if (paths[i] === 'organizations' || paths[i] === 'users') {
          return i + 1 < paths.length ? paths[i + 1] : '';
        }
      }
      return '';
    };

    window.addEventListener('popstate', () => {
      const orgId = getOrganizationFromUrlPath(location.pathname);
      if (this.props.state.lastAccessedOrganization?.item?.id !== orgId) {
        this.props.dependency.organizationUsecase.setLastAccessedOrganization(orgId);
        const matchedOrg = availableOrganizationsVar()?.find((org) => org.id === orgId);
        matchedOrg && globalState.setLastAccessedOrganization(matchedOrg);
      }
    });
  }

  componentDidCatch(err: Error) {
    this.props.dependency.errorReporter.reportError(err);
    this.props.dependency.errorPresenter.showAsFatal(err);
  }

  render() {
    const noHeadBar = !this.props.state.authentication.signedInUser || !!this.props.state.fatalError;

    return (
      <MuiPickersUtilsProvider dateAdapter={LuxonUtils}>
        <ConfigProvider value={this.props.config}>
          <ApolloProvider client={this.props.apolloClient}>
            <DependencyProvider value={this.props.dependency}>
              <HistoryProvider value={this.props.history}>
                <StateProvider value={this.props.state}>
                  <GoogleOauthProvider value={this.props.googleOauthService}>
                    <CustomRouter history={this.props.history}>
                      <MuiThemeProvider theme={theme}>
                        <CssBaseline />
                        <GlobalStyles />
                        <RootElement
                          style={{
                            paddingTop: noHeadBar ? 0 : TOPBAR_HEIGHT,
                          }}
                        >
                          <DocumentTitle />

                          <StyledSigningInIndicatorScreen
                            visible={this.props.state.authentication.fetching >= 1 && this.state.loading}
                          />

                          <NonFatalErrorSnackbars />

                          <FormattedSnackbar />

                          <ReactiveSnackbar />

                          <FatalErrorScreen />

                          <SignedOutScreen />

                          <SignedInScreen />
                        </RootElement>
                      </MuiThemeProvider>
                    </CustomRouter>
                  </GoogleOauthProvider>
                </StateProvider>
              </HistoryProvider>
            </DependencyProvider>
          </ApolloProvider>
        </ConfigProvider>
      </MuiPickersUtilsProvider>
    );
  }
}

const RootElement = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
});

const FatalErrorScreen = styled(_FatalErrorScreen)({
  width: '100%',
  height: '100%',
});

const FormattedSnackbar = styled(_FormattedSnackbar)({
  zIndex: theme.zIndex.snackbar,
});

const SignedOutScreen = styled(_SignedOutScreen)({
  width: '100%',
  height: '100%',
});

const SignedInScreen = styled(_SignedInScreen)({
  width: '100%',
  height: '100%',
});

const StyledSigningInIndicatorScreen = styled(SigningInIndicatorScreen)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000,
});

const CustomRouter = ({
  history,
  children,
  ...props
}: {
  history: History;
  children: React.ReactNode | React.ReactNode[];
}) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });
  const updateHistory = ({ action, location }: { action: History['action']; location: History['location'] }) =>
    setState((prev) => {
      return {
        action: action,
        location: { ...location, state: { prevPath: prev.location.pathname } },
      };
    });

  useLayoutEffect(() => history.listen(updateHistory), [history]);

  return (
    <Router {...props} location={state.location} navigationType={state.action} navigator={history}>
      {children}
    </Router>
  );
};

export default Root;
